import React, { useState, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import { useTranslation } from "react-i18next";
import saintsdb from "./data/saintsdb";
import saintsdbEn from "./data/saintsdb-en";
import saintsdbDe from "./data/saintsdb-de";
import saintsdbIt from "./data/saintsdb-it";
import saintsdbPtBr from "./data/saintsdb-pt-BR";
import RosaryIcon from "./RosaryIcon";
import "./Rosario.scss";
import "./Santos.scss";

const saintsdbByLang = {
  es: saintsdb,
  en: saintsdbEn,
  de: saintsdbDe,
  it: saintsdbIt,
  'pt-BR': saintsdbPtBr
};

const Santos = () => {
  const { t, i18n } = useTranslation();

  // Obtener la base de datos de santos según el idioma actual
  const saintsData = useMemo(() => {
    const lang = i18n.language || 'es';
    return saintsdbByLang[lang] || saintsdbByLang[lang.split('-')[0]] || saintsdb;
  }, [i18n.language]);

  // Estados
  const [view, setView] = useState("list"); // list, detail, prayer, novena
  const [selectedSaint, setSelectedSaint] = useState(null);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [selectedNovena, setSelectedNovena] = useState(null);
  const [novenaStep, setNovenaStep] = useState(0); // 0 = intro, 1-9 = días, 10 = completado

  // Handlers
  const handleSelectSaint = (saintId) => {
    const saint = saintsData.saints.find(s => s.saint_id === saintId);
    setSelectedSaint(saint);
    setView("detail");
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedSaint(null);
    setSelectedPrayer(null);
    setSelectedNovena(null);
    setNovenaStep(0);
  };

  const handleSelectPrayer = (prayerId) => {
    const prayer = selectedSaint.prayers.find(p => p.prayer_id === prayerId);
    setSelectedPrayer(prayer);
    setView("prayer");
  };

  const handleSelectNovena = (novenaId) => {
    const novena = selectedSaint.novenas.find(n => n.novena_id === novenaId);
    setSelectedNovena(novena);
    setNovenaStep(0);
    setView("novena");
  };

  const handleNovenaNext = () => {
    if (novenaStep < 9) {
      setNovenaStep(novenaStep + 1);
    } else {
      setNovenaStep(10); // Completado
    }
  };

  const handleNovenaPrevious = () => {
    if (novenaStep > 0) {
      setNovenaStep(novenaStep - 1);
    }
  };

  const handleNovenaReset = () => {
    setNovenaStep(0);
  };

  const getSaintIcon = (saintId) => {
    const icons = {
      virgin_mary: "👑",
      peter_apostle: "🗝️",
      paul_apostle: "📜",
      thomas_aquinas: "📚",
      john_bosco: "🧒",
      patrick: "🍀",
      augustine_hippo: "📖",
      pio_pietrelcina: "✝️",
      john_paul_ii: "🇵🇱",
      francis_assisi: "🐺",
      jude_thaddeus: "🔥",
      anthony_padua: "👶",
      michael_archangel: "⚔️",
      gabriel_archangel: "🎺",
      raphael_archangel: "💊",
      rita_cascia: "🌹",
      joseph: "🔨",
      monica: "👩",
      nicholas: "🎅",
      martin_tours: "🦌",
      isidro_labrador: "🌾",
      theresa_calcutta: "💕",
      ambrose: "🐝",
      gregory_great: "🎵",
      faustina: "💒"
    };
    if (saintId === "theresa_avila") {
      return <RosaryIcon width={24} height={36} className="saint-rosary-icon" />;
    }
    return icons[saintId] || "✝️";
  };

  // ===== VISTA DE LISTA DE SANTOS =====
  if (view === "list") {
    return (
      <div className="rosario-simple saints-list-view">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} className="text-center">
              <div className="mb-3">
                <h1 className="saint-title mb-2">{t("saints.title")}</h1>
                <p className="text-muted lead">{t("saints.subtitle")}</p>
              </div>

              <div className="saints-list">
                {saintsData.saints.map((saint) => (
                  <Card
                    key={saint.saint_id}
                    className="saint-card mb-3"
                    onClick={() => handleSelectSaint(saint.saint_id)}
                  >
                    <Card.Body className="d-flex align-items-center">
                      <div className="saint-icon me-3">
                        {getSaintIcon(saint.saint_id)}
                      </div>
                      <div className="flex-grow-1 text-start">
                        <h5 className="mb-1">{saint.name}</h5>
                        <p className="text-muted small mb-0">
                          {t("saints.feast_day")}: {t(`saints.feast_days.${saint.feast_day_key}`)}
                        </p>
                      </div>
                      <div className="text-end">
                        <i className="fa fa-chevron-right text-muted"></i>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE NOVENA - INTRO =====
  if (view === "novena" && novenaStep === 0) {
    return (
      <div className="rosario-simple">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="mb-4">
                <Button variant="link" className="text-muted" onClick={handleBackToList}>
                  ← {t("saints.back_to_list")}
                </Button>
              </div>

              <div className="mb-5">
                <div className="rosary-icon">{getSaintIcon(selectedSaint.saint_id)}</div>
                <h3 className="mb-2">{selectedSaint.name}</h3>
                <p className="text-muted">{selectedNovena.title_key ? t(selectedNovena.title_key) : selectedNovena.title}</p>
              </div>

              <Card className="text-center mystery-preview mb-4">
                <Card.Body>
                  <div className="mystery-icon-large"><RosaryIcon width={60} height={90} /></div>
                  <h4 className="mt-3">{t("saints.novena.title")}</h4>
                  <p className="text-muted mt-3">
                    {t("saints.novena.subtitle")}
                  </p>
                  <Badge bg="primary" className="mt-2">9 {t("saints.novena.days")}</Badge>
                </Card.Body>
              </Card>

              <p className="text-muted mb-4">
                <small>📖 {t("saints.novena.description")}</small>
              </p>

              <Button
                variant="primary"
                size="lg"
                className="w-100 py-3 start-button"
                onClick={handleNovenaNext}
              >
                {t("saints.novena.start")} →
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE NOVENA - COMPLETADO =====
  if (view === "novena" && novenaStep >= 10) {
    return (
      <div className="rosario-simple completion">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="completion-icon"><RosaryIcon width={80} height={120} /></div>
              <h1 className="mt-4">{t("saints.novena.completed.title")}</h1>
              <p className="lead">{t("saints.novena.completed.message")}</p>
              <p className="text-muted mb-4">{t("saints.novena.completed.blessing")}</p>

              <div className="d-grid gap-2">
                <Button variant="primary" size="lg" onClick={handleNovenaReset}>
                  {t("saints.novena.restart")}
                </Button>
                <Button variant="outline-secondary" onClick={() => setView("detail")}>
                  {t("saints.back_to_saint")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE NOVENA - ORACIÓN DEL DÍA =====
  if (view === "novena" && novenaStep > 0) {
    const currentDay = selectedNovena.days[novenaStep - 1];

    return (
      <div className="rosario-simple prayer-view">
        <Container className="rosario-container">
          <div className="prayer-content-wrapper">
            {/* Header */}
            <div className="rosario-header mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Button variant="link" className="text-muted p-0" onClick={handleNovenaReset}>
                  ← {t("saints.novena.back")}
                </Button>
                <div className="decade-counter">
                  <span className="decade-current">{novenaStep}</span>
                  <span className="decade-separator">/</span>
                  <span className="decade-total">9</span>
                </div>
              </div>

              <div className="mystery-label text-center">
                {selectedSaint.name}
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="decade-progress-container mb-4">
              {Array.from({ length: 9 }, (_, i) => {
                const progress = i < novenaStep - 1 ? 100 : 0;
                return (
                  <div
                    key={i}
                    className={`decade-segment ${i < novenaStep - 1 ? "completed" : ""} ${
                      i === novenaStep - 1 ? "active" : ""
                    }`}
                  >
                    <div className="decade-segment-fill" style={{ width: `${progress}%` }} />
                    <span className="decade-number">{i + 1}</span>
                  </div>
                );
              })}
            </div>

            {/* Tarjeta de oración */}
            <Card className="prayer-card-main" onClick={(e) => {
              if (!e.target.closest('button')) handleNovenaNext();
            }}>
              <Card.Body className="prayer-card-body">
                <div className="prayer-content">
                  <h2 className="prayer-title-main mb-3">
                    {currentDay.title_key ? t(currentDay.title_key) : currentDay.title}
                  </h2>
                  <div className="novena-intro mb-3">
                    <Badge bg="secondary">{t("saints.novena.day")} {novenaStep}</Badge>
                  </div>
                  <p className="prayer-text-main lead" dangerouslySetInnerHTML={{ __html: currentDay.prayer_key ? t(currentDay.prayer_key) : currentDay.prayer }} />
                  <div className="tap-indicator mt-4 text-muted">
                    <small>{t("rosary.simple.tap_continue")}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Controles */}
            <div className="navigation-controls d-flex justify-content-between gap-2 mt-4">
              <Button
                variant="outline-secondary"
                className="nav-btn flex-grow-1"
                onClick={handleNovenaPrevious}
                disabled={novenaStep === 1}
              >
                ← {t("rosary.simple.previous")}
              </Button>
              <Button
                variant="primary"
                className="nav-btn flex-grow-1"
                onClick={(e) => { e.stopPropagation(); handleNovenaNext(); }}
              >
                {t("rosary.simple.next")} →
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE ORACIÓN =====
  if (view === "prayer") {
    return (
      <div className="rosario-simple prayer-view">
        <Container className="rosario-container">
          <div className="prayer-content-wrapper">
            <div className="rosario-header mb-3">
              <Button variant="link" className="text-muted p-0" onClick={() => setView("detail")}>
                ← {t("saints.back_to_saint")}
              </Button>
              <div className="mystery-label text-center mt-2">
                {selectedSaint.name}
              </div>
            </div>

            <Card className="prayer-card-main">
              <Card.Body className="prayer-card-body">
                <div className="prayer-content">
                  <h2 className="prayer-title-main mb-4">{selectedPrayer.title}</h2>
                  <p className="prayer-text-main lead" dangerouslySetInnerHTML={{ __html: selectedPrayer.text }} />
                  <div className="prayer-ending mt-4 pt-3 border-top">
                    <p className="text-muted small mb-0">{t("saints.prayer_ending")}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE DETALLE DEL SANTO =====
  const hasNovena = selectedSaint.novenas && selectedSaint.novenas.length > 0;

  return (
    <div className="rosario-simple">
      <Container className="rosario-container py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="mb-4">
              <Button variant="link" className="text-muted" onClick={handleBackToList}>
                ← {t("saints.back_to_list")}
              </Button>
            </div>

            {/* Header del santo */}
            <Card className="saint-detail-header mb-4">
              <Card.Body className="text-center">
                <div className="saint-large-icon mb-3">
                  {getSaintIcon(selectedSaint.saint_id)}
                </div>
                <h2 className="mb-1">{selectedSaint.name}</h2>
                <p className="text-muted small mb-2">{selectedSaint.name_latin}</p>
                <p className="text-muted small mb-3">{selectedSaint.name_greek}</p>
                <Badge bg="primary">{t("saints.feast_day")}: {t(`saints.feast_days.${selectedSaint.feast_day_key}`)}</Badge>
              </Card.Body>
            </Card>

            {/* Biografía */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">{t("saints.biography")}</h5>
                <p className="mb-0">{selectedSaint.biography}</p>
              </Card.Body>
            </Card>

            {/* Oraciones */}
            {selectedSaint.prayers && selectedSaint.prayers.length > 0 && (
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">{t("saints.prayers")}</h5>
                  <div className="prayers-list">
                    {selectedSaint.prayers.map((prayer) => (
                      <Card
                        key={prayer.prayer_id}
                        className="prayer-item mb-2"
                        onClick={() => handleSelectPrayer(prayer.prayer_id)}
                      >
                        <Card.Body className="d-flex justify-content-between align-items-center py-2">
                          <span>{prayer.title}</span>
                          <i className="fa fa-chevron-right text-muted"></i>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Novenas */}
            {hasNovena && (
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">{t("saints.novenas")}</h5>
                  {selectedSaint.novenas.map((novena) => (
                    <Card
                      key={novena.novena_id}
                      className="novena-item mb-2"
                      onClick={() => handleSelectNovena(novena.novena_id)}
                    >
                      <Card.Body className="d-flex justify-content-between align-items-center py-2">
                        <div>
                          <div>{novena.title}</div>
                          <small className="text-muted">9 {t("saints.novena.days")}</small>
                        </div>
                        <i className="fa fa-chevron-right text-muted"></i>
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            )}

            {/* Oración final */}
            <Card className="prayer-footer-card">
              <Card.Body className="text-center">
                <p className="mb-0">{selectedSaint.name}, {t("saints.intercession")}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Santos;
